import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { productImageService } from '../../../services/productImageService';
import { API_BASE_URL } from '../../../services/api';
import { ImagePreviewDialog } from '../../common/components';
import { compressImageToLimit } from '../../../utils/imageUtils';
import type { ProductImage } from '../../../types/product';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

interface ProductImageManagerProps {
  productId: number;
}

export default function ProductImageManager({ productId }: ProductImageManagerProps) {
  const [images, setImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImageId, setPreviewImageId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenPreview = (imageId: number) => {
    setPreviewImageId(imageId);
  };

  const handlePreviewKeyDown = (e: React.KeyboardEvent, imageId: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenPreview(imageId);
    }
  };

  const handleClosePreview = () => {
    setPreviewImageId(null);
  };

  const fetchImages = async () => {
    try {
      setLoading(true);
      const data = await productImageService.getAll(productId);
      setImages(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Не вдалося завантажити зображення');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [productId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const originalFile = event.target.files[0];

      // Basic validation
      if (!originalFile.type.startsWith('image/')) {
        setError('Будь ласка, оберіть файл зображення');
        return;
      }

      try {
        setUploading(true);
        setError(null);
        const file = await compressImageToLimit(originalFile, MAX_IMAGE_BYTES);

        if (file.size > MAX_IMAGE_BYTES) {
          setError('Розмір зображення має бути менше 5МБ');
          return;
        }

        const newImage = await productImageService.upload(productId, file);
        setImages((prev) => [...prev, newImage]);
        
        // If it's the first image, refresh to ensure isMain status is correct (backend logic)
        if (images.length === 0) {
            fetchImages();
        }
      } catch (err) {
        console.error('Error uploading image:', err);
        setError('Не вдалося завантажити зображення');
      } finally {
        setUploading(false);
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleDelete = async (imageId: number) => {
    if (!window.confirm('Ви впевнені, що хочете видалити це зображення?')) return;

    try {
      await productImageService.delete(imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Не вдалося видалити зображення');
    }
  };

  const handleSetMain = async (imageId: number) => {
    try {
      await productImageService.setMain(imageId);
      // Update local state
      setImages((prev) =>
        prev.map((img) => ({
          ...img,
          isMain: img.id === imageId,
        }))
      );
    } catch (err) {
      console.error('Error setting main image:', err);
      setError('Не вдалося встановити головне зображення');
    }
  };

  if (loading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Зображення продукту</Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Завантаження...' : 'Завантажити зображення'}
        </Button>
        <input
          type="file"
          hidden
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileSelect}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {images.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4, bgcolor: 'background.default', borderRadius: 1 }}>
          Зображень ще не завантажено
        </Typography>
      ) : (
        <ImageList sx={{ width: '100%', maxHeight: 450 }} cols={3} rowHeight={164}>
          {images.map((item) => (
            <ImageListItem key={item.id}>
              <img
                src={`${API_BASE_URL}/images/${item.id}`}
                alt={item.fileName}
                loading="lazy"
                role="button"
                tabIndex={0}
                onClick={() => handleOpenPreview(item.id)}
                onKeyDown={(e) => handlePreviewKeyDown(e, item.id)}
                style={{
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'zoom-in',
                }}
              />
              <ImageListItemBar
                title={item.isMain ? "Головне зображення" : ""}
                actionIcon={
                  <Box sx={{ display: 'flex' }}>
                    <IconButton
                      sx={{ color: item.isMain ? 'yellow' : 'white' }}
                      aria-label={`star ${item.fileName}`}
                      onClick={() => handleSetMain(item.id)}
                    >
                      {item.isMain ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    <IconButton
                      sx={{ color: 'white' }}
                      aria-label={`delete ${item.fileName}`}
                      onClick={() => handleDelete(item.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      )}

      <ImagePreviewDialog
        imageId={previewImageId}
        onClose={handleClosePreview}
      />
    </Box>
  );
}
